import "../surfaces/surface-type-language.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/config/dp-config-schema.ts
/**
* The rules a DP config schema must meet (2026-10-12-shared-libraries, D15, R-008, R-009, R-018).
*
* Each DP version registers the JSON Schema of its config at `release-dp`, which reads it from the
* DP chart at the build record's digest (I6). release-engine stores it and checks every
* `dp_config_overrides` write for a data plane against the schema of the version that data plane
* runs (WS-4). The schema is untrusted input from a release, so release-engine compiles it in its
* own Ajv instance, with no `loadSchema`, only after {@link checkDpConfigSchema} admits it:
*
* 1. a JSON object at the root;
* 2. a nesting depth of at most {@link DP_CONFIG_SCHEMA_MAX_DEPTH} containers;
* 3. at most {@link DP_CONFIG_SCHEMA_MAX_BYTES} bytes of UTF-8 `JSON.stringify` text;
* 4. no `pattern` and no `patternProperties` key at any depth, so no regular expression from a
*    release runs on the CP;
* 5. every `$ref`, `$dynamicRef` and `$recursiveRef` value is a string that starts with `#`, so no
*    reference leaves the document;
* 6. every reference resolves inside the document, no reference chain returns to a node it left,
*    and the reference expansion holds at most {@link DP_CONFIG_SCHEMA_MAX_EXPANSION} containers.
*
* Rule 6 bounds the work of one Ajv validate call. Without it, a schema of 2,302 bytes, with nine
* `$defs` that each hold 12 `$ref` entries to the next one, passed rules 1 to 5 and took 349 ms per
* validate call; each further level multiplied the time by about 12 (contracts-WS-0-R4). The
* expansion of a schema is its count of JSON containers (objects and arrays) when each reference
* counts as a copy of every node it can name. A schema with no reference has an expansion equal to
* its container count, and rules 2 and 3 keep that count under the cap: the densest such schema,
* groups of 14 nested empty arrays under one array, holds 31,628 containers. So rule 6 refuses only
* a schema with a reference.
*
* A JSON pointer reference (`#` or `#/…`) can name a node under the document root or under any
* enclosing object with a string `$id`; rule 6 counts every node it can name, so a different `$id`
* reading in Ajv never makes the real count larger than the checked one. A plain-name reference
* (`#name`) names every object whose `$anchor` or `$dynamicAnchor` is `name`. So a recursive
* schema, for example `$recursiveRef: '#'` under the root, fails rule 6 as a cycle (DEF-3).
*
* `schemas/dp-config.meta.schema.json` states rules 1, 4 and 5 for an Ajv caller; JSON Schema
* cannot state a depth, a byte count or a reference graph, so rules 2, 3 and 6 live here alone. A
* test runs one corpus through both and requires the same answer on rules 1, 4 and 5.
*
* The walk reads keys, not schema positions: a config property named `pattern` also fails rule 4,
* and a `$ref` key inside a `default` value also counts for rule 6. That refuses more than the
* minimum, and it keeps the walk small (DEF-3).
*
* Pure and browser-safe.
*/
/** The deepest nesting a DP config schema may have, in JSON containers: 16, the surface cap (R-018). */
const DP_CONFIG_SCHEMA_MAX_DEPTH = 16;
/** The largest DP config schema: 65,536 bytes of UTF-8 `JSON.stringify` text (R-018). */
const DP_CONFIG_SCHEMA_MAX_BYTES = 65536;
/** The keys rule 4 refuses at any depth. */
const DP_CONFIG_SCHEMA_FORBIDDEN_KEYS = Object.freeze(["pattern", "patternProperties"]);
/**
* The largest reference expansion of a DP config schema (rule 6): 32,768 containers. It is above
* the 31,628 containers that the densest schema with no reference holds under rules 2 and 3, so
* every schema with no reference passes rule 6 (contracts-WS-0-R4).
*/
const DP_CONFIG_SCHEMA_MAX_EXPANSION = 32768;
/** The reference keys rule 5 restricts to a local `#` value. */
const DP_CONFIG_SCHEMA_REFERENCE_KEYS = Object.freeze([
	"$ref",
	"$dynamicRef",
	"$recursiveRef"
]);
/** Why {@link checkDpConfigSchema} refuses a schema, in check order. */
const DP_CONFIG_SCHEMA_REFUSALS = Object.freeze([
	"not_object",
	"too_deep",
	"oversize",
	"pattern_forbidden",
	"remote_ref",
	"ref_expansion"
]);
function isPlainObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** The path of the first container deeper than the cap, or `null`. The recursion stops at the cap. */
function tooDeepAt(value, depth, path) {
	if (typeof value !== "object" || value === null) return null;
	if (depth > DP_CONFIG_SCHEMA_MAX_DEPTH) return path;
	const entries = Array.isArray(value) ? value.map((item, index) => [String(index), item]) : Object.entries(value);
	for (const [key, child] of entries) {
		const found = tooDeepAt(child, depth + 1, [...path, key]);
		if (found !== null) return found;
	}
	return null;
}
/** The first rule 4 or rule 5 failure under `value`, or `null`. */
function keywordFailureAt(value, path) {
	if (typeof value !== "object" || value === null) return null;
	if (Array.isArray(value)) {
		for (const [index, item] of value.entries()) {
			const found = keywordFailureAt(item, [...path, String(index)]);
			if (found !== null) return found;
		}
		return null;
	}
	for (const [key, child] of Object.entries(value)) {
		const here = [...path, key];
		if (DP_CONFIG_SCHEMA_FORBIDDEN_KEYS.includes(key)) return {
			reason: "pattern_forbidden",
			path: here
		};
		if (DP_CONFIG_SCHEMA_REFERENCE_KEYS.includes(key) && !(typeof child === "string" && child.startsWith("#"))) return {
			reason: "remote_ref",
			path: here
		};
		const found = keywordFailureAt(child, here);
		if (found !== null) return found;
	}
	return null;
}
/** The keys whose string value names an anchor for a `#name` reference. */
const ANCHOR_KEYS = Object.freeze(["$anchor", "$dynamicAnchor"]);
/** Records every container under `value`. The depth is at most 16 here, because rule 2 ran first. */
function indexNodes(value, path, bases, index) {
	if (typeof value !== "object" || value === null) return;
	if (Array.isArray(value)) {
		index.nodes.set(value, {
			path,
			bases
		});
		value.forEach((item, position) => indexNodes(item, [...path, String(position)], bases, index));
		return;
	}
	const record = value;
	const here = typeof record.$id === "string" ? [value, ...bases] : bases;
	index.nodes.set(value, {
		path,
		bases: here
	});
	for (const key of ANCHOR_KEYS) {
		const name = record[key];
		if (typeof name !== "string") continue;
		const carriers = index.anchors.get(name) ?? [];
		carriers.push(value);
		index.anchors.set(name, carriers);
	}
	for (const [key, child] of Object.entries(record)) indexNodes(child, [...path, key], here, index);
}
/** The node that the RFC 6901 pointer `pointer` names under `base`, or `undefined`. */
function followPointer(base, tokens) {
	let node = base;
	for (const token of tokens) if (Array.isArray(node)) {
		if (!/^(?:0|[1-9][0-9]*)$/.test(token) || Number(token) >= node.length) return void 0;
		node = node[Number(token)];
	} else if (isPlainObject(node) && Object.hasOwn(node, token)) node = node[token];
	else return;
	return node;
}
/**
* The RFC 6901 reference tokens of the pointer fragment `fragment` (the text after `#`, which
* starts with `/`), in the order Ajv's `getJsonPointer` reads them: split the raw fragment on `/`,
* then percent-decode each token, then unescape `~1` to `/` and `~0` to `~`. A percent-escaped
* slash (`%2F`) therefore stays inside its token and names a key that holds a `/`, as in Ajv. A
* decode of the whole fragment before the split names a different node (S1 WS-0-R4). Returns
* `null` when a token holds a malformed percent escape, which Ajv cannot resolve either.
*/
function pointerTokens(fragment) {
	const tokens = [];
	for (const raw of fragment.slice(1).split("/")) {
		let token;
		try {
			token = decodeURIComponent(raw);
		} catch {
			return null;
		}
		tokens.push(token.replaceAll("~1", "/").replaceAll("~0", "~"));
	}
	return tokens;
}
/**
* Every node the local reference `reference` can name from a node with the bases `bases`, or an
* empty list when it names none. A primitive target is a boolean schema or a value, and it costs
* nothing, so the list holds containers only, plus `null` for a resolved primitive.
*
* A pointer reference (`#` or `#/…`) resolves token by token as {@link pointerTokens} reads it. A
* plain-name reference (`#name`) names every object whose anchor is the percent-decoded name.
*/
function referenceTargets(reference, bases, index) {
	const raw = reference.slice(1);
	if (raw !== "" && !raw.startsWith("/")) {
		let name;
		try {
			name = decodeURIComponent(raw);
		} catch {
			return [];
		}
		return index.anchors.get(name) ?? [];
	}
	const tokens = raw === "" ? [] : pointerTokens(raw);
	if (tokens === null) return [];
	const targets = [];
	for (const base of bases) {
		const target = followPointer(base, tokens);
		if (target === void 0) continue;
		targets.push(typeof target === "object" ? target : null);
	}
	return targets;
}
/**
* The rule 6 failure of `schema`, as the path it names, or `null`. The walk counts each container
* once per copy that a reference makes, memoizes the count of each finished container, and stops
* at the first failure, so it reads each container and each reference a bounded number of times.
*
* The walk keeps its own stack, post-order, and never recurses: a chain of references can be
* thousands of hops long under the byte cap, and a call per hop overflows the call stack with a
* `RangeError` before the cap check runs (S1 contracts re-judge, architecture-1). A container is
* open from its push to its pop; a reference to an open container is a cycle.
*/
function referenceFailureAt(schema) {
	const index = {
		nodes: /* @__PURE__ */ new Map(),
		anchors: /* @__PURE__ */ new Map()
	};
	indexNodes(schema, [], [schema], index);
	const finished = /* @__PURE__ */ new Map();
	const open = /* @__PURE__ */ new Set();
	const stack = [];
	const push = (node) => {
		open.add(node);
		stack.push({
			node,
			entry: index.nodes.get(node),
			keys: Object.entries(node),
			nextKey: 0,
			parts: [],
			nextPart: 0,
			total: 1
		});
	};
	push(schema);
	for (;;) {
		const frame = stack[stack.length - 1];
		if (frame.nextPart < frame.parts.length) {
			const part = frame.parts[frame.nextPart];
			frame.nextPart += 1;
			if (part === null) continue;
			const known = finished.get(part);
			if (known === void 0) {
				push(part);
				continue;
			}
			frame.total += known;
			if (frame.total > 32768) return [];
			continue;
		}
		if (frame.nextKey < frame.keys.length) {
			const [key, child] = frame.keys[frame.nextKey];
			frame.nextKey += 1;
			const parts = typeof child === "object" ? [child] : [];
			if (DP_CONFIG_SCHEMA_REFERENCE_KEYS.includes(key)) {
				const targets = referenceTargets(String(child), frame.entry.bases, index);
				if (targets.length === 0 || targets.some((target) => target !== null && open.has(target))) return [...frame.entry.path, key];
				parts.push(...targets);
			}
			frame.parts = parts;
			frame.nextPart = 0;
			continue;
		}
		stack.pop();
		open.delete(frame.node);
		finished.set(frame.node, frame.total);
		const parent = stack[stack.length - 1];
		if (parent === void 0) return null;
		parent.total += frame.total;
		if (parent.total > 32768) return [];
	}
}
/**
* Checks an untrusted DP config schema against the six rules of this module, in order, and gives
* the first failure with the path of the offending key. A `ref_expansion` failure names the
* reference key for an unresolvable reference or a cycle, and the root (`[]`) for an expansion over
* the cap. Never throws for a JSON value. Pure.
*/
function checkDpConfigSchema(schema) {
	if (!isPlainObject(schema)) return {
		ok: false,
		reason: "not_object",
		path: []
	};
	const deep = tooDeepAt(schema, 1, []);
	if (deep !== null) return {
		ok: false,
		reason: "too_deep",
		path: deep
	};
	if (utf8ToBytes(JSON.stringify(schema)).length > 65536) return {
		ok: false,
		reason: "oversize",
		path: []
	};
	const keyword = keywordFailureAt(schema, []);
	if (keyword !== null) return {
		ok: false,
		reason: keyword.reason,
		path: keyword.path
	};
	const expansion = referenceFailureAt(schema);
	if (expansion !== null) return {
		ok: false,
		reason: "ref_expansion",
		path: expansion
	};
	return { ok: true };
}
//#endregion
export { DP_CONFIG_SCHEMA_FORBIDDEN_KEYS, DP_CONFIG_SCHEMA_MAX_BYTES, DP_CONFIG_SCHEMA_MAX_DEPTH, DP_CONFIG_SCHEMA_MAX_EXPANSION, DP_CONFIG_SCHEMA_REFERENCE_KEYS, DP_CONFIG_SCHEMA_REFUSALS, checkDpConfigSchema };

//# sourceMappingURL=dp-config-schema.js.map